import { resourceUrl } from "@/core/services/api.service";
import store from "@/core/services/store";
import JwtService from "@/core/services/jwt.service";

const chatBotApiService = {
  /**
   * Sends a message to the HelpBot and processes the streamed response
   * @param {Object} params - The parameters for the API call
   * @param {string} params.inputText - The user's input text
   * @param {string} [params.conversationId] - Optional: The conversation ID
   * @param {Function} params.onChunk - Callback function for each stream chunk
   * @param {Function} [params.onComplete] - Optional: Callback when stream is complete
   * @param {Function} [params.onError] - Optional: Callback for errors
   * @param {AbortController} [params.abortController] - Optional: AbortController
   * @returns {Promise<void>}
   */
  async sendMessage({
    inputText,
    conversationId,
    onChunk,
    onComplete,
    onError,
    abortController = null
  }) {
    try {
      const payload = { input_as_text: inputText };
      if (conversationId) payload.conversation_id = conversationId;
      this.setHeaders();

      const fetchOptions = {
        method: "POST",
        headers: this.headers,
        body: JSON.stringify(payload)
      };
      if (abortController) fetchOptions.signal = abortController.signal;

      const response = await fetch(
        resourceUrl.value + "/helpbot",
        fetchOptions
      );

      if (!response.ok) return;

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let isComplete = false;
      let lineBuffer = "";

      /**
       * Helper function to parse a line of streamed data
       * @returns {Object|null}
       */
      const parseLineData = line => {
        if (line.trim() === "") return null;

        if (line.startsWith("data: ")) {
          const data = line.substring(6).trim();
          if (data === "[DONE]") return "[DONE]"; // Signal for completion

          try {
            return JSON.parse(data);
          } catch (parseError) {
            return { text: data };
          }
        } else {
          return { text: line };
        }
      };

      try {
        while (!isComplete) {
          const { done, value } = await reader.read();

          // If the stream is done, process any remaining buffer
          if (done) {
            if (lineBuffer.trim()) {
              const result = parseLineData(lineBuffer);
              if (result && result !== "[DONE]") onChunk([result]); // Send last buffer
            }
            if (onComplete) onComplete();
            break;
          }

          const chunk = lineBuffer + decoder.decode(value, { stream: true });
          const lines = chunk.split("\n");

          lineBuffer = lines.pop();

          const batch = [];

          for (const line of lines) {
            if (line.trim() === "") continue;

            const result = parseLineData(line);

            if (result === "[DONE]") {
              // If DONE signal, send the remaining batch and complete
              if (batch.length > 0) onChunk(batch);
              if (onComplete) onComplete();
              isComplete = true;
              break;
            } else if (result) {
              // Accumulate results in a batch
              batch.push(result);
            }
          }

          // Send accumulated batch
          if (batch.length > 0) {
            onChunk(batch);
          }

          if (isComplete) break;
        }
      } finally {
        reader.releaseLock();
      }
    } catch (error) {
      if (onError) onError(error);
      else throw error;
    }
  },

  /**
   * Creates a new UUID for conversation identification
   * @returns {string} A new UUID string
   */
  generateConversationId() {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
      /[xy]/g,
      function (c) {
        const r = (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      }
    );
  },

  /**
   * Helper function to cancel an ongoing streaming request
   * @param {AbortController} controller - The AbortController to cancel
   */
  cancelStream(controller) {
    // Get the current AbortController from the store if none is provided,
    // Needed for external calls
    const currentAbortController =
      controller ?? store.getters.abortController.abortController;

    if (currentAbortController && !currentAbortController.signal.aborted) {
      currentAbortController.abort();
    }
  },

  /**
   * Sets the headers for the API requests
   */
  setHeaders() {
    this.headers = {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
      "Cache-Control": "no-cache"
    };

    const projectId = store.getters.selectedProject?.id;
    if (projectId) {
      this.headers["x-project-id"] = projectId;
    }

    const presetVersion = store.getters.selectedPresetVersion;
    if (presetVersion) {
      this.headers["x-preset-version"] = presetVersion.id;
    }

    if (JwtService.getToken()) {
      this.headers["Authorization"] = `Bearer ${JwtService.getToken()}`;
    }
  }
};

export default chatBotApiService;
